import { SortPriceType } from '../../../@types/sort';

export type GerenciadorSortOption = DataItem & {
  sortValue?: string;
  priceType?: SortPriceType;
};

type GerenciadorSortOptions = Record<'productId' | 'price' | 'cpi' | 'newMargin' | 'priceRetail' | 'competitorsPrice', GerenciadorSortOption[]>;

export const sortOptionsColumnMap: Record<string, string> = {
  productId: 'productId',
  productDescription: 'productId',
  productFamilyId: 'productId',

  price: 'price',
  priceRange: 'price',
  priceOffer: 'price',
  priceRangeOffer: 'price',

  cpi: 'cpi',
  cpiRange: 'cpi',
  cpiOffer: 'cpi',
  cpiRangeOffer: 'cpi',

  newMargin: 'newMargin',
  newMarginRange: 'newMargin',
  newMarginOffer: 'newMargin',
  newMarginRangeOffer: 'newMargin',

  priceRetail: 'priceRetail',
  priceRetailOffer: 'priceRetail',

  competitorsPrice: 'competitorsPrice',
  competitorsPriceOffer: 'competitorsPrice',
};

const regularOptions: GerenciadorSortOptions = {
  productId: [
    {
      value: 'productId',
      label: 'Código do produto',
    },
    {
      value: 'productDescription',
      label: 'Descrição do produto',
    },
    {
      value: 'productFamilyId',
      label: 'Código da família',
    },
  ],
  price: [
    {
      value: 'price',
      label: 'Preço sugerido',
      sortValue: 'price',
      priceType: 'REGULAR',
    },
    {
      value: 'priceRange',
      label: 'Variação de preço',
      sortValue: 'priceRange',
      priceType: 'REGULAR',
    },
  ],
  cpi: [
    {
      value: 'cpi',
      label: 'Nova competitividade',
      sortValue: 'cpi',
      priceType: 'REGULAR',
    },
    {
      value: 'cpiRange',
      label: 'Variação de competitividade',
      sortValue: 'cpiRange',
      priceType: 'REGULAR',
    },
  ],
  newMargin: [
    {
      value: 'newMargin',
      label: 'Nova margem',
      sortValue: 'newMargin',
      priceType: 'REGULAR',
    },
    {
      value: 'newMarginRange',
      label: 'Variação de margem',
      sortValue: 'newMarginRange',
      priceType: 'REGULAR',
    },
  ],
  priceRetail: [
    {
      value: 'priceRetail',
      label: 'Preço regular vigente',
      sortValue: 'priceRetail',
      priceType: 'REGULAR',
    },
  ],
  competitorsPrice: [
    {
      value: 'competitorsPrice',
      label: 'Preço regular concorrente',
      sortValue: 'competitorsPrice',
      priceType: 'REGULAR',
    },
  ],
};

const farmaOptions: GerenciadorSortOptions = {
  ...regularOptions,
  price: [
    {
      value: 'price',
      label: 'Preço regular sugerido',
      sortValue: 'price',
      priceType: 'REGULAR',
    },
    {
      value: 'priceRange',
      label: 'Variação de preço regular',
      sortValue: 'priceRange',
      priceType: 'REGULAR',
    },
    {
      value: 'priceOffer',
      label: 'Preço oferta sugerido',
      sortValue: 'price',
      priceType: 'OFFER',
    },
    {
      value: 'priceRangeOffer',
      label: 'Variação do preço oferta',
      sortValue: 'priceRange',
      priceType: 'OFFER',
    },
  ],
  cpi: [
    {
      value: 'cpi',
      label: 'Nova CPI do preço regular',
      sortValue: 'cpi',
      priceType: 'REGULAR',
    },
    {
      value: 'cpiRange',
      label: 'Variação de CPI do preço regular',
      sortValue: 'cpiRange',
      priceType: 'REGULAR',
    },
    {
      value: 'cpiOffer',
      label: 'Nova CPI do preço oferta',
      sortValue: 'cpi',
      priceType: 'OFFER',
    },
    {
      value: 'cpiRangeOffer',
      label: 'Variação de CPI do preço oferta',
      sortValue: 'cpiRange',
      priceType: 'OFFER',
    },
  ],
  newMargin: [
    {
      value: 'newMargin',
      label: 'Nova margem do preço regular',
      sortValue: 'newMargin',
      priceType: 'REGULAR',
    },
    {
      value: 'newMarginRange',
      label: 'Variação de margem do preço regular',
      sortValue: 'newMarginRange',
      priceType: 'REGULAR',
    },
    {
      value: 'newMarginOffer',
      label: 'Nova margem do preço oferta',
      sortValue: 'newMargin',
      priceType: 'OFFER',
    },
    {
      value: 'newMarginRangeOffer',
      label: 'Variação de margem do preço oferta',
      sortValue: 'newMarginRange',
      priceType: 'OFFER',
    },
  ],
  priceRetail: [
    {
      value: 'priceRetail',
      label: 'Preço regular vigente',
      sortValue: 'priceRetail',
      priceType: 'REGULAR',
    },
    {
      value: 'priceRetailOffer',
      label: 'Preço oferta vigente',
      sortValue: 'priceRetail',
      priceType: 'OFFER',
    },
  ],
  competitorsPrice: [
    {
      value: 'competitorsPrice',
      label: 'Preço regular concorrente',
      sortValue: 'competitorsPrice',
      priceType: 'REGULAR',
    },
    {
      value: 'competitorsPriceOffer',
      label: 'Preço oferta concorrente',
      sortValue: 'competitorsPrice',
      priceType: 'OFFER',
    },
  ],
};

export const getSortOptions = (isFarmaClient = false): GerenciadorSortOptions => {
  return isFarmaClient ? farmaOptions : regularOptions;
};

export const getSortOptionValue = (column: keyof GerenciadorSortOptions, sortValue: string, priceType: SortPriceType = 'REGULAR'): string | undefined => {
  const option = getSortOptions(true)[column].find(item => (item.sortValue || item.value) === sortValue && (item.priceType || 'REGULAR') === priceType);

  return option?.value;
};

const options = regularOptions;

export default options;
